import type { ChangeEvent } from "react";

/**
 * Backend is the source of truth for this rule (App\Rules\EnglishName) -
 * keep this pattern in sync with it. Allows English letters, spaces,
 * hyphens, and apostrophes (e.g. "Anne-Marie", "O'Brien"); rejects Arabic
 * and any other non-English characters, plus leading/trailing/duplicate
 * separators (e.g. "  John", "--John", "John  Doe").
 */
export const ENGLISH_NAME_REGEX = /^[A-Za-z]+(?:[\s'-][A-Za-z]+)*$/;

export function isEnglishName(value: string): boolean {
  return ENGLISH_NAME_REGEX.test(value);
}

/** Strips any character outside the allowed name charset (letters, spaces, hyphens, apostrophes) as the user types. */
export function sanitizeEnglishNameInput(value: string): string {
  return value.replace(/[^A-Za-z\s'-]/g, "");
}

/**
 * Wraps a react-hook-form `register()` field so disallowed characters
 * (Arabic, digits, symbols, ...) can never actually be typed into the input,
 * instead of only surfacing as a validation error after submit.
 */
export function withEnglishNameFilter<
  T extends { onChange: (event: ChangeEvent<HTMLInputElement>) => unknown },
>(field: T): T {
  return {
    ...field,
    onChange: (event: ChangeEvent<HTMLInputElement>) => {
      const sanitized = sanitizeEnglishNameInput(event.target.value);
      if (sanitized !== event.target.value) {
        event.target.value = sanitized;
      }
      return field.onChange(event);
    },
  };
}

/**
 * Splits a single "Full Name" box into the first/last pair the API requires.
 *
 * Everything before the first space is the first name, everything after is the
 * last - so "Ahmed Ali Hassan" gives "Ahmed" and "Ali Hassan" rather than
 * dropping the middle part. Returns null when the input cannot make a valid
 * pair (no space, or either side under the 3-character minimum), which is what
 * lets the form report the problem on the one field the user can see.
 */
export function splitFullName(value: string): { fname: string; lname: string } | null {
  const trimmed = value.trim().replace(/\s+/g, " ");
  const firstSpace = trimmed.indexOf(" ");

  if (firstSpace === -1) {
    return null;
  }

  const fname = trimmed.slice(0, firstSpace);
  const lname = trimmed.slice(firstSpace + 1);

  if (fname.length < 3 || lname.length < 3) {
    return null;
  }

  return { fname, lname };
}
